import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, AlertTriangle, Calendar, TrendingUp, DollarSign } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { AttentionStrip } from '../../components/ui';
import { dashboardAPI, equipmentAPI } from '../../services/api';
import { format, isValid } from 'date-fns';
import OperationsTabs from './OperationsTabs';
import { isServiceDueSoon } from './serviceDue';

function safeFormatDate(value) {
  if (value == null || value === '') return '-';
  const d = new Date(value);
  return isValid(d) ? format(d, 'dd MMM yyyy') : '-';
}

export default function OperationsDashboard() {
  const [summary, setSummary] = useState(null);
  const [serviceDueCount, setServiceDueCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    dashboardAPI.getEquipmentSummary()
      .then((res) => {
        setSummary(res?.data != null ? res.data : {});
        setLoadError(null);
      })
      .catch((err) => {
        console.error('Equipment summary load failed:', err);
        setSummary({});
        setLoadError(err?.message || 'Unable to load data');
      })
      .finally(() => setLoading(false));

    // GA-012: count service-due equipment using client-side derived dates so
    // items with an interval but no persisted next_service_due still alert.
    equipmentAPI.list({ status: 'ACTIVE', page: 1, limit: 200 })
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        setServiceDueCount(rows.filter((eq) => isServiceDueSoon(eq)).length);
      })
      .catch(() => setServiceDueCount(0));
  }, []);

  if (loading) return <DashboardLayout><div className="py-12 text-center text-ga-text-muted">Loading...</div></DashboardLayout>;

  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const dueIn7Days = safeArray(summary?.dueIn7Days);
  const overdue = safeArray(summary?.overdue);
  const underRepair = safeArray(summary?.underRepair);
  const mostUsedEquipment = safeArray(summary?.mostUsedEquipment);
  const monthlyMaintenanceCost = Number(summary?.monthlyMaintenanceCost ?? 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
            <Wrench className="h-7 w-7 text-ga-primary" />
            Operations
          </h1>
          <p className="text-ga-text-muted mt-1">Daily log, equipment and maintenance in one place</p>
          {loadError && (
            <p className="mt-2 text-sm text-ga-danger">{loadError}</p>
          )}
        </div>

        <OperationsTabs active="maintenance" />

        {serviceDueCount > 0 && (
          <AttentionStrip
            icon={AlertTriangle}
            tone="warning"
            title={`${serviceDueCount} equipment item${serviceDueCount === 1 ? '' : 's'} due for service`}
            message="Next service is overdue or falls within the next 7 days."
            action={
              <Link to="/dashboard/assets/fixed" className="ga-btn-primary px-3 py-1.5 text-sm">
                View fixed assets
              </Link>
            }
          />
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="ga-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-ga-text-muted">Due in 7 Days</p>
                <p className="text-2xl font-bold text-ga-warning">{dueIn7Days.length}</p>
              </div>
              <Calendar className="h-8 w-8 text-ga-warning" />
            </div>
          </div>
          <div className="ga-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-ga-text-muted">Overdue</p>
                <p className="text-2xl font-bold text-ga-danger">{overdue.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-ga-danger" />
            </div>
          </div>
          <div className="ga-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-ga-text-muted">Under Repair</p>
                <p className="text-2xl font-bold text-ga-text-muted">{underRepair.length}</p>
              </div>
              <Wrench className="h-8 w-8 text-ga-text-subtle" />
            </div>
          </div>
          <div className="ga-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-ga-text-muted">Monthly Cost</p>
                <p className="text-2xl font-bold text-ga-primary">₹{monthlyMaintenanceCost.toLocaleString()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-ga-primary" />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="ga-card border-ga-warning/30 overflow-hidden">
            <div className="px-4 py-2 border-b border-ga-warning/30 bg-ga-warning/10">
              <h2 className="font-semibold text-ga-warning">Due in 7 Days</h2>
            </div>
            <div className="divide-y divide-ga-border-subtle max-h-48 overflow-y-auto">
              {dueIn7Days.length === 0 ? (
                <div className="p-4 text-ga-text-muted text-sm">None</div>
              ) : (
                dueIn7Days.map((eq, idx) => (
                  <Link key={eq?.id ?? `due-${idx}`} to={`/dashboard/assets/fixed/${eq?.id ?? ''}`} className="block p-3 hover:bg-ga-primary-soft/20">
                    <p className="font-medium text-ga-text-primary">{eq?.equipment_name ?? '–'}</p>
                    <p className="text-sm text-ga-text-muted">{safeFormatDate(eq.next_service_due)}</p>
                  </Link>
                ))
              )}
            </div>
          </div>

          <div className="ga-card border-ga-danger/30 overflow-hidden">
            <div className="px-4 py-2 border-b border-ga-danger/30 bg-ga-danger/10">
              <h2 className="font-semibold text-ga-danger">Overdue</h2>
            </div>
            <div className="divide-y divide-ga-border-subtle max-h-48 overflow-y-auto">
              {overdue.length === 0 ? (
                <div className="p-4 text-ga-text-muted text-sm">None</div>
              ) : (
                overdue.map((eq, idx) => (
                  <Link key={eq?.id ?? `overdue-${idx}`} to={`/dashboard/assets/fixed/${eq?.id ?? ''}`} className="block p-3 hover:bg-ga-danger/10">
                    <p className="font-medium text-ga-text-primary">{eq?.equipment_name ?? '–'}</p>
                    <p className="text-sm text-ga-danger">{safeFormatDate(eq.next_service_due)}</p>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="ga-card overflow-hidden">
          <div className="px-4 py-2 border-b border-ga-border-subtle bg-ga-bg-page">
            <h2 className="font-semibold text-ga-text-primary flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Most Used Equipment (30 days)
            </h2>
          </div>
          <div className="divide-y divide-ga-border-subtle">
            {mostUsedEquipment.length === 0 ? (
              <div className="p-4 text-ga-text-muted text-sm">No data</div>
            ) : (
              mostUsedEquipment.map((eq, idx) => (
                <Link key={eq?.id ?? `most-${idx}`} to={`/dashboard/assets/fixed/${eq?.id ?? ''}`} className="flex items-center justify-between p-4 hover:bg-ga-primary-soft/10">
                  <div>
                    <p className="font-medium text-ga-text-primary">{eq?.equipment_name ?? '–'}</p>
                    <p className="text-sm text-ga-text-muted">{eq?.asset_code ?? ''}</p>
                  </div>
                  <p className="font-semibold text-ga-primary">{Number(eq.total_hours || 0).toFixed(1)} hrs</p>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
